import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Underline, List, ListOrdered, Link2, Unlink,
  AlignLeft, AlignCenter, AlignRight, Heading2, Heading3,
  Eraser, Undo2, Redo2, Variable,
} from "lucide-react";

const COULEURS = [
  ["#071A2F", "Texte foncé"], ["#282194", "Violet CFHTB"], ["#409AC3", "Bleu CFHTB"],
  ["#1E8E5A", "Vert"], ["#D97706", "Orange"], ["#DC2626", "Rouge"], ["#6B7280", "Gris"],
];

export function RichTextEditor({ content = "", onUpdate, variables = null, testid = "wysiwyg", minHeight = 160 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content,
    onUpdate: ({ editor: e }) => onUpdate?.(e.getHTML()),
  });

  const cls = (active) =>
    `p-1.5 rounded transition-colors ${active ? "bg-cfhtb-bg text-cfhtb-primary" : "text-cfhtb-text hover:bg-slate-200"}`;

  const setLink = () => {
    const url = window.prompt("URL du lien (https://…)", "https://");
    if (url && url !== "https://") editor?.chain().focus().setLink({ href: url }).run();
  };

  const Btn = ({ id, title, active, onClick, children }) => (
    <button type="button" data-testid={`${testid}-${id}`} title={title} onClick={onClick} className={cls(active)}>
      {children}
    </button>
  );

  return (
    <div className="rounded-md border border-slate-300 overflow-hidden focus-within:ring-2 focus-within:ring-cfhtb-light">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-[#F7F7F7] px-2 py-1.5" data-testid={`${testid}-toolbar`}>
        <Btn id="h2" title="Titre" active={editor?.isActive("heading", { level: 2 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </Btn>
        <Btn id="h3" title="Sous-titre" active={editor?.isActive("heading", { level: 3 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" />
        </Btn>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <Btn id="bold" title="Gras" active={editor?.isActive("bold")}
          onClick={() => editor?.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </Btn>
        <Btn id="italic" title="Italique" active={editor?.isActive("italic")}
          onClick={() => editor?.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </Btn>
        <Btn id="underline" title="Souligné" active={editor?.isActive("underline")}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}>
          <Underline className="h-4 w-4" />
        </Btn>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <Btn id="list" title="Liste à puces" active={editor?.isActive("bulletList")}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </Btn>
        <Btn id="ordered" title="Liste numérotée" active={editor?.isActive("orderedList")}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </Btn>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <Btn id="link" title="Insérer un lien" active={editor?.isActive("link")} onClick={setLink}>
          <Link2 className="h-4 w-4" />
        </Btn>
        <Btn id="unlink" title="Retirer le lien" active={false}
          onClick={() => editor?.chain().focus().unsetLink().run()}>
          <Unlink className="h-4 w-4" />
        </Btn>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <Btn id="align-left" title="Aligner à gauche" active={editor?.isActive({ textAlign: "left" })}
          onClick={() => editor?.chain().focus().setTextAlign("left").run()}>
          <AlignLeft className="h-4 w-4" />
        </Btn>
        <Btn id="align-center" title="Centrer" active={editor?.isActive({ textAlign: "center" })}
          onClick={() => editor?.chain().focus().setTextAlign("center").run()}>
          <AlignCenter className="h-4 w-4" />
        </Btn>
        <Btn id="align-right" title="Aligner à droite" active={editor?.isActive({ textAlign: "right" })}
          onClick={() => editor?.chain().focus().setTextAlign("right").run()}>
          <AlignRight className="h-4 w-4" />
        </Btn>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <span className="inline-flex items-center gap-1" data-testid={`${testid}-colors`}>
          {COULEURS.map(([hex, label]) => (
            <button key={hex} type="button" data-testid={`${testid}-color-${hex.slice(1)}`} title={label}
              onClick={() => editor?.chain().focus().setColor(hex).run()}
              className="h-5 w-5 rounded-full border border-slate-300 hover:scale-110 transition-transform"
              style={{ backgroundColor: hex }} />
          ))}
        </span>
        <Btn id="clear" title="Effacer le formatage" active={false}
          onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}>
          <Eraser className="h-4 w-4" />
        </Btn>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <Btn id="undo" title="Annuler" active={false} onClick={() => editor?.chain().focus().undo().run()}>
          <Undo2 className="h-4 w-4" />
        </Btn>
        <Btn id="redo" title="Rétablir" active={false} onClick={() => editor?.chain().focus().redo().run()}>
          <Redo2 className="h-4 w-4" />
        </Btn>
        {variables && (
          <>
            <span className="mx-1 h-5 w-px bg-slate-300" />
            <span className="inline-flex items-center gap-1 text-xs text-cfhtb-text/70 mr-1">
              <Variable className="h-3.5 w-3.5" /> Variables :
            </span>
            {variables.map(([token, label]) => (
              <button key={token} type="button" data-testid={`${testid}-var-${token.replace(/[{}]/g, "")}`}
                onClick={() => editor?.chain().focus().insertContent(token).run()}
                className="text-xs px-2 py-1 rounded bg-cfhtb-bg text-cfhtb-primary font-medium hover:bg-cfhtb-light/40 transition-colors">
                {label}
              </button>
            ))}
          </>
        )}
      </div>
      <EditorContent
        editor={editor}
        data-testid={testid}
        style={{ minHeight }}
        className="px-4 py-3 text-sm text-cfhtb-text [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h2]:font-serif [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-cfhtb-heading [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:font-serif [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-cfhtb-heading [&_h3]:mt-2 [&_a]:text-cfhtb-secondary [&_a]:underline [&_.ProseMirror]:outline-none"
      />
    </div>
  );
}
